import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation, Outlet } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { qk } from '../services/queryKeys';
import { fetchSpaces, fetchUsage, fetchNotifications, markNotificationsRead, fetchBilling, fetchRecentItems } from '../services/api';
import { useAppStore } from '../store/useAppStore';
import { logoutUser } from '../services/auth';
import { redirectToPlatformAuth } from './platformAuthRedirect';
import Icon from '../components/Icon';
import Avatar from '../components/Avatar';
import ToastHost from '../components/Toast';
import UploadModal from '../screens/UploadModal';
import UploadDock from '../components/UploadDock';
import SearchResults from '../components/SearchResults';
import { PLAN_META } from '../screens/Billing';
import type { Space } from '../types';

function fmtBytes(b: number): string {
  if (!b) return '0 B';
  const u = ['B', 'KB', 'MB', 'GB', 'TB'];
  let i = 0, n = b;
  while (n >= 1024 && i < u.length - 1) { n /= 1024; i++; }
  return (n >= 100 || i === 0 ? Math.round(n) : n.toFixed(1)) + ' ' + u[i];
}

export default function WebShell() {
  const { isAuthenticated, isAdmin, viewMode, setViewMode, setIsAdmin, displayName, firstName, email, clearSession } = useAppStore();
  const navigate = useNavigate();
  const location = useLocation();

  const [notifOpen, setNotifOpen] = useState(false);
  const [accountOpen, setAccountOpen] = useState(false);
  const [mobileNavOpen, setMobileNavOpen] = useState(false);
  const [searchQ, setSearchQ] = useState('');
  const [searchOpen, setSearchOpen] = useState(false);
  const notifRef = useRef<HTMLDivElement>(null);
  const acctRef = useRef<HTMLDivElement>(null);
  const searchRef = useRef<HTMLDivElement>(null);

  const { data: spaces = [] } = useQuery({
    queryKey: qk.spaces(),
    queryFn: fetchSpaces,
    enabled: isAuthenticated,
  });

  const { data: usage } = useQuery({
    queryKey: qk.usage(),
    queryFn: fetchUsage,
    enabled: isAuthenticated,
  });

  const { data: notifications = [] } = useQuery({
    queryKey: qk.notifications(),
    queryFn: fetchNotifications,
    enabled: isAuthenticated,
    refetchInterval: 30000,
  });

  const { data: billing } = useQuery({
    queryKey: ['billing'],
    queryFn: fetchBilling,
    enabled: isAuthenticated,
  });

  // Warm the "Recent" cache as soon as the shell mounts, so navigating there
  // renders instantly instead of waiting on a fetch to start from scratch.
  useQuery({
    queryKey: qk.recent(),
    queryFn: fetchRecentItems,
    enabled: isAuthenticated,
  });
  const planId = billing?.plan?.current_plan_id ?? 'free';
  const planLabel = PLAN_META[planId]?.label ?? planId;

  const unread = notifications.filter((n: any) => !n.read).length;

  useEffect(() => {
    function handleClick(e: MouseEvent) {
      if (notifRef.current && !notifRef.current.contains(e.target as Node)) setNotifOpen(false);
      if (acctRef.current && !acctRef.current.contains(e.target as Node)) setAccountOpen(false);
      if (searchRef.current && !searchRef.current.contains(e.target as Node)) setSearchOpen(false);
    }
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, []);

  // Escape closes the results panel without clearing what was typed.
  useEffect(() => {
    if (!searchOpen) return;
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') setSearchOpen(false); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [searchOpen]);

  // Auth is fully handled by RootGate (session probe + redirect) before this
  // component ever mounts — this guard is just a defensive fallback.
  if (!isAuthenticated) return null;

  const view = location.pathname.split('/')[1] ?? 'browse';
  const teamSpaces = (spaces ?? []).filter((s: Space) => s.type === 'team');

  const usedBytes = usage?.used_bytes ?? 0;
  const quotaBytes = usage?.quota_bytes ?? 50 * 1024 * 1024 * 1024;
  const storagePct = quotaBytes > 0 ? Math.min(100, Math.round((usedBytes / quotaBytes) * 100)) : 0;

  function navTo(path: string) {
    navigate('/' + path);
    setMobileNavOpen(false);
  }

  function isActive(v: string) {
    return view === v || (view === '' && v === 'browse');
  }

  async function handleSignOut() {
    setAccountOpen(false);
    try {
      await logoutUser();
    } finally {
      clearSession();
      redirectToPlatformAuth();
    }
  }

  const currentRole = isAdmin ? 'admin' : 'member';

  return (
    <>
      <div className="sk-shell" style={{ display: 'flex', height: '100vh', overflow: 'hidden', background: 'var(--paper)' }}>

        {/* ── Mobile nav backdrop (only visible when the drawer is open) ── */}
        {mobileNavOpen && (
          <div className="sk-nav-backdrop" onClick={() => setMobileNavOpen(false)} />
        )}

        {/* ── Sidebar ── */}
        <aside className={`sk-sidebar${mobileNavOpen ? ' is-open' : ''}`} style={{ width: 248, flexShrink: 0, borderRight: '1px solid var(--line)', background: 'var(--paper)', display: 'flex', flexDirection: 'column', height: '100%' }}>

          {/* Logo */}
          <div style={{ padding: '16px 16px 10px', display: 'flex', alignItems: 'center', gap: 10 }}>
            <div style={{ width: 30, height: 30, borderRadius: 8, background: 'var(--accent)', display: 'grid', placeItems: 'center', boxShadow: 'var(--shadow-sm)', flexShrink: 0 }}>
              <Icon name="hdd" size={17} color="#fff" />
            </div>
            <div style={{ lineHeight: 1.15, whiteSpace: 'nowrap', minWidth: 0 }}>
              <div style={{ fontWeight: 700, fontSize: 'var(--fs-lg)', letterSpacing: '-0.01em' }}>SK Drive</div>
              <div style={{ fontSize: 'var(--fs-xs)', color: 'var(--ink-3)' }}>
                {viewMode === 'personal' ? 'Plus plan' : 'Softknack'}
              </div>
            </div>
          </div>

          {/* Scrollable nav */}
          <div style={{ flex: 1, overflowY: 'auto', padding: '6px 10px', display: 'flex', flexDirection: 'column', gap: 1 }}>

            {/* My Drive */}
            <button
              className={`t-nav-row${isActive('browse') || isActive('') ? ' is-active' : ''}`}
              onClick={() => navTo('browse')}
            >
              <Icon name="hdd" size={16} />
              <span style={{ flex: 1 }}>My Drive</span>
            </button>

            {/* Team spaces */}
            {viewMode === 'workspace' && teamSpaces.length > 0 && (
              <>
                <div className="t-nav-label">Team spaces</div>
                {teamSpaces.map((space: Space) => (
                  <button
                    key={space.id}
                    className={`t-nav-row${view === `space/${space.id}` ? ' is-active' : ''}`}
                    onClick={() => navTo(`space/${space.id}`)}
                    style={{ paddingLeft: 28 }}
                  >
                    <Icon name="users" size={16} />
                    <span style={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{space.name}</span>
                  </button>
                ))}
              </>
            )}

            {/* Divider */}
            <div style={{ height: 1, background: 'var(--line)', margin: '8px 0' }} />

            {/* Recent / Shared / Starred / Trash — no section label */}
            <button className={`t-nav-row${isActive('recent') ? ' is-active' : ''}`} onClick={() => navTo('recent')}>
              <Icon name="clock" size={16} />
              <span style={{ flex: 1 }}>Recent</span>
            </button>
            {viewMode === 'workspace' && (
              <button className={`t-nav-row${isActive('shared') ? ' is-active' : ''}`} onClick={() => navTo('shared')}>
                <Icon name="users" size={16} />
                <span style={{ flex: 1 }}>Shared with me</span>
              </button>
            )}
            <button className={`t-nav-row${isActive('starred') ? ' is-active' : ''}`} onClick={() => navTo('starred')}>
              <Icon name="star" size={16} />
              <span style={{ flex: 1 }}>Starred</span>
            </button>
            <button className={`t-nav-row${isActive('trash') ? ' is-active' : ''}`} onClick={() => navTo('trash')}>
              <Icon name="trash" size={16} />
              <span style={{ flex: 1 }}>Trash</span>
            </button>

            {/* Send & receive */}
            <div className="t-nav-label">Send &amp; receive</div>
            <button className={`t-nav-row${isActive('requests') ? ' is-active' : ''}`} onClick={() => navTo('requests')}>
              <Icon name="inbox" size={16} />
              <span style={{ flex: 1 }}>File requests</span>
            </button>
            <button className={`t-nav-row${isActive('transfers') ? ' is-active' : ''}`} onClick={() => navTo('transfers')}>
              <Icon name="send" size={16} />
              <span style={{ flex: 1 }}>Transfers</span>
            </button>
          </div>

          {/* Bottom section: storage meter + admin */}
          <div style={{ borderTop: '1px solid var(--line)', padding: '10px' }}>

            {/* Storage meter button */}
            <button
              onClick={() => navTo('storage')}
              style={{
                width: '100%', border: 'none',
                background: isActive('storage') ? 'var(--accent-soft)' : 'transparent',
                borderRadius: 'var(--r-2)', padding: '8px 10px', cursor: 'pointer', textAlign: 'left',
              }}
            >
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 6 }}>
                <span style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 'var(--fs-sm)', fontWeight: 'var(--fw-medium)', color: 'var(--ink-2)' }}>
                  <Icon name="database" size={14} />Storage
                </span>
                <span style={{ fontSize: 'var(--fs-xs)', color: 'var(--ink-3)' }}>{storagePct}%</span>
              </div>
              <div style={{ height: 6, borderRadius: 999, background: 'var(--paper-3)', overflow: 'hidden' }}>
                <div style={{ width: `${storagePct}%`, height: '100%', background: storagePct > 90 ? 'var(--danger)' : 'var(--accent)', borderRadius: 999 }} />
              </div>
              <div style={{ fontSize: 'var(--fs-xs)', color: 'var(--ink-3)', marginTop: 6 }}>
                {fmtBytes(usedBytes)} of {fmtBytes(quotaBytes)}
              </div>
            </button>

            {/* Plan & billing */}
            <button className="t-upgrade-banner" onClick={() => navTo('account')}>
              <Icon name="crown" size={20} color="#FCD34D" />
              <span style={{ flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', fontWeight: 700, fontSize: 'var(--fs-md)' }}>
                Plan &amp; billing
              </span>
              <span className="t-upgrade-pill">{planLabel}</span>
            </button>

            {/* Workspace admin: Administration section */}
            {viewMode === 'workspace' && isAdmin && (
              <>
                <div className="t-nav-label">Administration</div>
                <button className={`t-nav-row${isActive('members') ? ' is-active' : ''}`} onClick={() => navTo('members')}>
                  <Icon name="users" size={16} />
                  <span style={{ flex: 1 }}>Members</span>
                </button>
                <button className={`t-nav-row${isActive('storagereq') ? ' is-active' : ''}`} onClick={() => navTo('storagereq')}>
                  <Icon name="inbox" size={16} />
                  <span style={{ flex: 1 }}>Storage requests</span>
                </button>
                <button className={`t-nav-row${isActive('admin') ? ' is-active' : ''}`} onClick={() => navTo('admin')}>
                  <Icon name="layers" size={16} />
                  <span style={{ flex: 1 }}>Storage management</span>
                </button>
              </>
            )}
          </div>
        </aside>

        {/* ── Main area ── */}
        <div style={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden', minWidth: 0 }}>

          {/* TopBar */}
          <header className="sk-topbar" style={{ height: 56, flexShrink: 0, borderBottom: '1px solid var(--line)', background: 'var(--card)', display: 'flex', alignItems: 'center', gap: 14, padding: '0 18px' }}>

            {/* Hamburger — opens the nav drawer on mobile */}
            <button
              className="sk-hamburger t-btn t-btn--ghost t-btn--sm t-btn--icon"
              aria-label="Open menu"
              onClick={() => setMobileNavOpen(true)}
            >
              <Icon name="menu" size={18} />
            </button>

            {/* Search */}
            <div ref={searchRef} className="sk-search" style={{ position: 'relative', flex: 1, maxWidth: 520 }}>
              <span style={{ position: 'absolute', left: 10, top: '50%', transform: 'translateY(-50%)', color: 'var(--ink-3)', pointerEvents: 'none' }}>
                <Icon name="search" size={14} />
              </span>
              <input
                className="t-input"
                placeholder="Search in SK Drive…"
                value={searchQ}
                onChange={e => { setSearchQ(e.target.value); setSearchOpen(true); }}
                onFocus={() => setSearchOpen(true)}
                style={{ paddingLeft: 34 }}
              />
              {searchQ && (
                <button
                  onClick={() => { setSearchQ(''); setSearchOpen(false); }}
                  aria-label="Clear"
                  style={{ position: 'absolute', right: 8, top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', cursor: 'pointer', color: 'var(--ink-3)', display: 'grid', placeItems: 'center' }}
                >
                  <Icon name="x" size={13} />
                </button>
              )}
              {searchOpen && (
                <SearchResults
                  query={searchQ}
                  onClose={() => setSearchOpen(false)}
                  // Land in the folder holding the hit, with the item selected.
                  onPick={hit => navigate(`/space/${hit.space_id}`, {
                    state: { folderId: hit.folder_id ?? undefined, highlightItemId: hit.id },
                  })}
                />
              )}
            </div>

            <div style={{ flex: 1 }} />

            {/* Standalone: Upgrade */}
            {viewMode === 'personal' && (
              <button className="t-btn t-btn--accent t-btn--sm" onClick={() => navTo('account')}>
                <Icon name="zap" size={12} />
                Upgrade
              </button>
            )}

            {/* Workspace: Member / Admin segmented toggle */}
            {viewMode === 'workspace' && (
              <div style={{ display: 'flex', gap: 4, background: 'var(--paper-2)', border: '1px solid var(--line)', borderRadius: 999, padding: 3 }}>
                {(['member', 'admin'] as const).map(r => (
                  <button
                    key={r}
                    onClick={() => setIsAdmin(r === 'admin')}
                    style={{
                      border: 'none', cursor: 'pointer', padding: '4px 12px', borderRadius: 999,
                      fontSize: 'var(--fs-sm)', fontWeight: 'var(--fw-medium)', textTransform: 'capitalize',
                      fontFamily: 'var(--font-sans)',
                      background: currentRole === r ? 'var(--card)' : 'transparent',
                      color: currentRole === r ? 'var(--ink)' : 'var(--ink-3)',
                      boxShadow: currentRole === r ? 'var(--shadow-sm)' : 'none',
                      transition: 'background var(--t-fast), color var(--t-fast)',
                    }}
                  >{r}</button>
                ))}
              </div>
            )}

            {/* Notification bell */}
            <div style={{ position: 'relative' }} ref={notifRef}>
              <button
                onClick={() => { setNotifOpen(v => !v); if (!notifOpen) markNotificationsRead(); }}
                aria-label="Notifications"
                className="t-btn t-btn--ghost t-btn--sm t-btn--icon"
                style={{ position: 'relative' }}
              >
                <Icon name="bell" size={16} />
                {unread > 0 && (
                  <span style={{ position: 'absolute', top: 3, right: 3, minWidth: 15, height: 15, padding: '0 3px', borderRadius: 999, background: 'var(--danger)', color: '#fff', fontSize: 9, fontWeight: 700, display: 'grid', placeItems: 'center', border: '1.5px solid var(--card)' }}>
                    {unread}
                  </span>
                )}
              </button>
              {notifOpen && (
                <>
                  <div onClick={() => setNotifOpen(false)} style={{ position: 'fixed', inset: 0, zIndex: 50 }} />
                  <div style={{ position: 'absolute', top: 'calc(100% + 8px)', right: 0, zIndex: 51, width: 340, background: 'var(--card)', border: '1px solid var(--line)', borderRadius: 'var(--r-3)', boxShadow: 'var(--shadow-lg)', overflow: 'hidden' }}>
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '12px 14px', borderBottom: '1px solid var(--line)' }}>
                      <strong style={{ fontSize: 'var(--fs-md)' }}>Notifications</strong>
                      {unread > 0 && (
                        <button onClick={() => markNotificationsRead()} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--accent)', fontSize: 'var(--fs-sm)', fontWeight: 'var(--fw-medium)' }}>
                          Mark all read
                        </button>
                      )}
                    </div>
                    <div style={{ maxHeight: 380, overflowY: 'auto' }}>
                      {notifications.length === 0 ? (
                        <div style={{ padding: '24px 14px', textAlign: 'center', color: 'var(--ink-3)', fontSize: 'var(--fs-sm)' }}>No notifications</div>
                      ) : notifications.slice(0, 8).map((n: any) => (
                        <div
                          key={n.id}
                          style={{ display: 'flex', gap: 11, border: 'none', borderBottom: '1px solid var(--line)', background: n.read ? 'var(--card)' : 'var(--accent-soft)', cursor: 'pointer', padding: '11px 14px' }}
                        >
                          <div style={{ flex: 1, minWidth: 0 }}>
                            <div style={{ fontSize: 'var(--fs-md)', color: 'var(--ink-2)', lineHeight: 1.4 }}>{n.text}</div>
                            <div style={{ fontSize: 'var(--fs-xs)', color: 'var(--ink-3)', marginTop: 2 }}>
                              {n.created_at ? new Date(n.created_at).toLocaleDateString() : ''}
                            </div>
                          </div>
                          {!n.read && <span style={{ width: 8, height: 8, borderRadius: 999, background: 'var(--accent)', marginTop: 5, flexShrink: 0 }} />}
                        </div>
                      ))}
                    </div>
                  </div>
                </>
              )}
            </div>

            {/* Account button + dropdown */}
            <div style={{ position: 'relative' }} ref={acctRef}>
              <button
                onClick={() => setAccountOpen(v => !v)}
                style={{ display: 'flex', alignItems: 'center', gap: 8, border: 'none', background: 'none', cursor: 'pointer', padding: '3px 4px', borderRadius: 'var(--r-2)' }}
              >
                <Avatar name={displayName || firstName || email || 'U'} size="sm" />
                <div className="sk-acct-text" style={{ lineHeight: 1.1, textAlign: 'left' }}>
                  <div style={{ fontSize: 'var(--fs-sm)', fontWeight: 'var(--fw-medium)', color: 'var(--ink)' }}>
                    {displayName || firstName || 'User'}
                  </div>
                  <div style={{ fontSize: 'var(--fs-xs)', color: 'var(--ink-3)' }}>
                    {viewMode === 'personal' ? 'Plus plan' : isAdmin ? 'Workspace admin' : 'Member'}
                  </div>
                </div>
                <Icon name="chev-d" size={13} color="var(--ink-3)" className="sk-acct-text" />
              </button>

              {accountOpen && (
                <>
                  <div onClick={() => setAccountOpen(false)} style={{ position: 'fixed', inset: 0, zIndex: 50 }} />
                  <div style={{ position: 'absolute', top: 'calc(100% + 8px)', right: 0, zIndex: 51, width: 248, background: 'var(--card)', border: '1px solid var(--line)', borderRadius: 'var(--r-3)', boxShadow: 'var(--shadow-lg)', padding: 6 }}>

                    {/* Header */}
                    <div style={{ padding: '8px 9px 9px', borderBottom: '1px solid var(--line)', marginBottom: 5 }}>
                      <div style={{ fontWeight: 'var(--fw-semibold)', fontSize: 'var(--fs-md)' }}>{displayName || firstName}</div>
                      <div style={{ fontSize: 'var(--fs-xs)', color: 'var(--ink-3)' }}>
                        {viewMode === 'personal' ? email : 'Softknack workspace'}
                      </div>
                    </div>

                    {viewMode === 'personal' ? (
                      <>
                        {([
                          { label: 'Plan & billing', icon: 'trend', action: () => navTo('account') },
                          { label: 'Account settings', icon: 'settings', action: () => {} },
                          { label: 'Manage on Softknack', icon: 'external', action: () => {} },
                        ] as const).map(item => (
                          <button
                            key={item.label}
                            onClick={() => { setAccountOpen(false); item.action(); }}
                            style={{ display: 'flex', alignItems: 'center', gap: 9, width: '100%', border: 'none', background: 'none', cursor: 'pointer', padding: '8px 9px', borderRadius: 'var(--r-2)', fontSize: 'var(--fs-md)', color: 'var(--ink-2)', textAlign: 'left', fontFamily: 'var(--font-sans)' }}
                            onMouseEnter={e => { (e.currentTarget as HTMLButtonElement).style.background = 'var(--paper-2)'; }}
                            onMouseLeave={e => { (e.currentTarget as HTMLButtonElement).style.background = 'none'; }}
                          >
                            <Icon name={item.icon} size={15} color="var(--ink-3)" />
                            {item.label}
                          </button>
                        ))}
                        <div style={{ height: 1, background: 'var(--line)', margin: '5px 4px' }} />
                        <button
                          onClick={handleSignOut}
                          style={{ display: 'flex', alignItems: 'center', gap: 9, width: '100%', border: 'none', background: 'none', cursor: 'pointer', padding: '8px 9px', borderRadius: 'var(--r-2)', fontSize: 'var(--fs-md)', color: 'var(--ink-2)', textAlign: 'left', fontFamily: 'var(--font-sans)' }}
                          onMouseEnter={e => { (e.currentTarget as HTMLButtonElement).style.background = 'var(--paper-2)'; }}
                          onMouseLeave={e => { (e.currentTarget as HTMLButtonElement).style.background = 'none'; }}
                        >
                          <Icon name="arrow-l" size={15} color="var(--ink-3)" />
                          Sign out
                        </button>
                      </>
                    ) : (
                      <>
                        <div style={{ display: 'flex', gap: 8, padding: '6px 9px 9px', fontSize: 'var(--fs-xs)', color: 'var(--ink-3)' }}>
                          <Icon name="info" size={13} style={{ flexShrink: 0, marginTop: 1 }} />
                          You're viewing the shared team workspace.
                        </div>
                        <button
                          onClick={handleSignOut}
                          style={{ display: 'flex', alignItems: 'center', gap: 9, width: '100%', border: 'none', background: 'none', cursor: 'pointer', padding: '8px 9px', borderRadius: 'var(--r-2)', fontSize: 'var(--fs-md)', color: 'var(--ink-2)', textAlign: 'left', fontFamily: 'var(--font-sans)' }}
                          onMouseEnter={e => { (e.currentTarget as HTMLButtonElement).style.background = 'var(--paper-2)'; }}
                          onMouseLeave={e => { (e.currentTarget as HTMLButtonElement).style.background = 'none'; }}
                        >
                          <Icon name="arrow-l" size={15} color="var(--ink-3)" />
                          Exit to home &amp; switch account
                        </button>
                      </>
                    )}
                  </div>
                </>
              )}
            </div>
          </header>

          {/* Content */}
          <main style={{ flex: 1, overflow: 'hidden', display: 'flex', flexDirection: 'column', minHeight: 0 }}>
            <Outlet />
          </main>
        </div>
      </div>

      <ToastHost />
      <UploadModal />
      <UploadDock />
    </>
  );
}
